<template>
  <b-card
    ref="popupCard"
    :title="title"
    :sub-title="subtitle"
    :style="{ '--font-family': fontFamily, 'font-family': fontFamily }"
    data-cy="popup-content"
    class="popup-content"
  >
    <hr v-if="title || subtitle" class="w-100" />
    <p v-if="xyConfig" class="total">{{ xyConfig.xAxisTickLabel }}</p>
    <p v-else-if="showTotal" class="total">
      {{ totalLabel }}{{ totalString }}
      <span v-if="numProjects && showProjectCount">
        ({{ numProjects }}&nbsp;{{ projectString }})
      </span>
    </p>

    <PopupPieGraphTable
      v-if="!disableChart && config.showChart && node && !isXYBubbles"
      :id="graphId"
      :node="node"
      :story-colour="storyColour"
    />

    <div v-if="node" class="mt-3">
      <template v-if="!isMobile">
        <CategoryItem
          v-for="(item, index) in categoryItems"
          :key="index"
          :item="item"
          :node="node"
        />
      </template>
      <template v-if="isXYBubbles && xyConfig">
        <CategoryItem :item="xyConfig.bubbleSizeColumn" :node="node" :xy-config="xyConfig" />
        <CategoryItem
          v-if="xyConfig.yAxisColumn !== xyConfig.bubbleSizeColumn"
          :item="xyConfig.yAxisColumn"
          :label="yLabel"
          :node="node"
          :xy-config="xyConfig"
        />
      </template>
    </div>
  </b-card>
</template>

<script lang="ts">
import Vue, { PropType } from 'vue'
import Component from 'vue-class-component'

import CategoryItem from '@/components/public/Popup/CategoryItem.vue'
import PopupPieGraphTable from '@/components/public/GraphTable/PopupPieGraphTable.vue'

import {
  PopupDetails,
  MapPopupDetails,
  PopupConfigTypes,
  PopupConfigType,
  FieldTypes,
} from '@/types'
import { XYVisNode } from '../Visualisations/XYBubbles/types'
import { COMPARISON_POPUP_CONFIG_KEY, POPUP_CONFIG_KEY } from '@/constants'

const components = {
  CategoryItem,
  PopupPieGraphTable,
}
const props = {
  popupDetails: {
    type: Object as PropType<PopupDetails | MapPopupDetails | undefined>,
  },
  disableChart: {
    type: Boolean,
    default: false,
  },
  graphId: {
    type: String,
    default: 'popupGraph',
  },
  titleProp: {
    type: String,
    default: null,
  },
  isMobile: {
    type: Boolean,
    default: false,
  },
}

@Component({ components })
export default class PopupContent extends Vue.extend({ props }) {
  updated() {
    this.$emit('updated', {
      clientHeight: this.popupCard.clientHeight,
    })
  }

  get configKey() {
    if (this.node?.isComparison) {
      return COMPARISON_POPUP_CONFIG_KEY
    }

    return POPUP_CONFIG_KEY
  }

  get popupCard() {
    return this.$refs.popupCard as HTMLElement
  }

  get versionConfig() {
    return this.$typedStore.activeVisualisation.visualisationStore.app.config
  }

  get isXYBubbles() {
    return this.$typedStore.activeVisualisation.isXYBubbles
  }

  get xyNode() {
    return this.popupDetails?.node as XYVisNode
  }

  get yLabel() {
    const { xyBubblesConfig } = this.versionConfig
    return xyBubblesConfig?.yAxisTicks.useDistinctValues
      ? xyBubblesConfig.yAxisTicks.customLabels.find(
          (label) => label.value === this.xyNode?.yAxisValue,
        )?.text
      : null
  }

  get xyConfig() {
    return this.$typedStore.visualisationApp.xyDimensionsMappings.find(
      (mapping) => mapping.xAxisTickKey === this.xyNode?.xAxisTickKey,
    )
  }

  get mode(): PopupConfigType {
    return this.versionConfig[this.configKey].levels
      ? PopupConfigTypes.PER_LEVEL
      : PopupConfigTypes.GLOBAL
  }

  get config() {
    const config = this.versionConfig[this.configKey]
    return config.global ?? (config.levels && config.levels[this.level]) ?? {}
  }

  get app() {
    return this.$typedStore.activeVisualisation.visualisationStore.app
  }

  get hidePiechartOnZeroNegative() {
    return this.versionConfig.appConfig.hidePiechartOnZeroNegative
  }

  get showProjectCount() {
    return this.versionConfig.appConfig.showProjectCount
  }

  get projectPlural() {
    return this.versionConfig.appConfig.projectPlural
  }

  get projectSingular() {
    return this.versionConfig.appConfig.projectSingular
  }

  get formatValueByColumn() {
    return this.$typedStore.visualisationData.formatValueByColumn
  }

  get node() {
    return this.popupDetails?.node
  }

  get storyColour() {
    const path = this.node?.path.level1 ?? this.$typedStore.activeVisualisation.storyPath
    return this.$typedStore.activeVisualisation.visualisationStore.entities.nodes.colour(path)
  }

  get categoryItems() {
    if (!this.isXYBubbles) {
      return this.config.items
    }

    return [
      ...(this.config.items?.filter((item) =>
        this.versionConfig.fieldMap.find((f) => f.column === item && f.type === FieldTypes.STRING),
      ) ?? []),
      ...[],
    ]
  }

  get fontFamily() {
    return this.versionConfig.storySettings.sidebarBot.fontFamily
  }

  get level() {
    // default to leaf level to handle nodes from map view as they may not have a path but we want to display leaf level data
    return this.node?.path ? this.node.path.level : this.$typedStore.activeVisualisation.leafLevel
  }

  get isLeafLevel() {
    return this.level === this.$typedStore.activeVisualisation.leafLevel
  }

  get levelField() {
    return this.node?.path && this.app.levels.fieldForLevel(this.node.path.level)
  }

  get title() {
    if (this.titleProp !== null) {
      return this.titleProp
    } else {
      let titleField
      if (this.isLeafLevel || this.mode === PopupConfigTypes.PER_LEVEL) {
        titleField = this.config.titleField
      } else {
        titleField = this.levelField?.column
      }
      return titleField && this.node?.data ? this.node.data[titleField] : ''
    }
  }

  get subtitle() {
    if (this.isLeafLevel || this.mode === PopupConfigTypes.PER_LEVEL) {
      const subtitleField = this.config.subtitleField
      return subtitleField && this.node?.data ? this.node.data[subtitleField] : ''
    } else {
      return this.levelField?.name ?? ''
    }
  }

  get showTotal() {
    return this.config.showTotal ?? true
  }

  get totalLabel() {
    const { totalStringLabel } =
      this.$typedStore.activeVisualisation.visualisationStore.app.config.appConfig
    return totalStringLabel ? `${totalStringLabel}: ` : ''
  }

  get totalString() {
    if (this.node) return this.node.totalString
  }

  get numProjects() {
    const mapPopupDetails = this.popupDetails as MapPopupDetails
    if (mapPopupDetails) {
      return mapPopupDetails.projectCount
    }

    return 0
  }

  get projectString() {
    return this.numProjects > 1 ? this.projectPlural : this.projectSingular
  }
}
</script>

<style lang="scss">
/* NB Unscoped */

.popup-content .card-title,
.popup-content .card-subtitle {
  font-family: var(--font-family);
  text-align: center;
}

.popup-content .card-title {
  /* bootstrap black */
  color: #212529;
}

.popup-content.card {
  box-shadow: 0px 0px 10px 2px rgba(65, 65, 65, 0.8);
  border-radius: var(--border-radius);
}

.popup-content hr {
  margin-top: 0.5rem;
  margin-bottom: 0.5rem;
}
.popup-content {
  .card-body {
    display: flex;
    flex-direction: column;
    .card-subtitle {
      color: #455 !important;
      font-weight: 600;
      font-size: 16px;
      font-family: inherit;
    }
  }
}
</style>
